import { zodResolver } from "@hookform/resolvers/zod"
import {
  type UseQueryResult,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"
import {
  createRootRoute,
  createRoute,
  createRouter,
  RouterProvider,
  redirect,
} from "@tanstack/react-router"
import { format } from "date-fns"
import {
  DownloadCloud,
  GraduationCap,
  KeyRound,
  Languages,
  LogOut,
  PlugZap,
  ShieldCheck,
  Trash2,
  UserRound,
} from "lucide-react"
import { lazy, Suspense, useEffect, useState } from "react"
import { Helmet } from "react-helmet-async"
import { Controller, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { z } from "zod"

import { AuthFrame } from "./components/auth-frame"
import { LearnerShell } from "./components/learner-shell"
import { PageAccess, PageEmpty, PageError, PageLoading } from "./components/page-state"
import { PolicyDocument } from "./components/policy-document"
import { RecentActivity } from "./components/recent-activity"
import { Badge } from "./components/ui/badge"
import { Button } from "./components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "./components/ui/card"
import { Checkbox } from "./components/ui/checkbox"
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from "./components/ui/field"
import { Input } from "./components/ui/input"
import { Progress } from "./components/ui/progress"
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./components/ui/select"
import { Separator } from "./components/ui/separator"

import {
  ApiError,
  apiOrigin,
  cancelAccountDeletion,
  completeOnboarding,
  consumeVerificationToken,
  exportAccountData,
  type GuardianRelationship,
  getAccountDeletionPreview,
  getAccountDeletionStatus,
  getAccountProviderLinkStatus,
  getAuthorizedClients,
  getGuardianRelationships,
  getGuardianSummary,
  getLearnerMe,
  getLearningAnalytics,
  getLearningPath,
  getLinkedProviders,
  getMastery,
  getOnboarding,
  getWebSession,
  type LearnerMe,
  type Mastery,
  type MasteryItem,
  registerAccount,
  requestAccountDeletion,
  requestDeletionStatusChallenge,
  requestGuardianChallenge,
  requestPasswordReset,
  resetPassword,
  reviewGuardianChallenge,
  revokeAuthorizedClient,
  revokeGuardianConsent,
  sendVerificationEmail,
  signOut,
  startAccountProviderLink,
  unlinkAccountProvider,
  updateLocale,
  verifyDeletionStatusChallenge,
  verifyGuardianChallenge,
  type WebSession,
} from "./lib/api"
import { billingStatusQueryOptions } from "./lib/billing-status"
import { type LearnerPage, resolveLearnerAccess } from "./lib/learner-access"

const PathPage = lazy(() =>
  import("./pages/learning-pages").then((module) => ({ default: module.PathPage })),
)
const AnalyticsPage = lazy(() =>
  import("./pages/learning-pages").then((module) => ({ default: module.AnalyticsPage })),
)
const BillingPage = lazy(() =>
  import("./pages/account-pages").then((module) => ({ default: module.BillingPage })),
)
const FeedbackPage = lazy(() =>
  import("./pages/account-pages").then((module) => ({ default: module.FeedbackPage })),
)
const SupportPage = lazy(() =>
  import("./pages/account-pages").then((module) => ({ default: module.SupportPage })),
)
const AdminSupportPage = lazy(() =>
  import("./pages/account-pages").then((module) => ({ default: module.AdminSupportPage })),
)
const AdminSettingsPage = lazy(() =>
  import("./pages/admin-pages").then((module) => ({ default: module.AdminSettingsPage })),
)
const AdminUsersPage = lazy(() =>
  import("./pages/admin-pages").then((module) => ({ default: module.AdminUsersPage })),
)
const AdminAuditPage = lazy(() =>
  import("./pages/admin-pages").then((module) => ({ default: module.AdminAuditPage })),
)

export function App({ appRouter = router }: { appRouter?: typeof router }) {
  return <RouterProvider router={appRouter} />
}

const rootRoute = createRootRoute({ notFoundComponent: NotFound })
const overviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: () => <LearnerApp page="home" />,
})
const masteryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/mastery",
  component: () => <LearnerApp page="mastery" />,
})
const accountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/account",
  component: () => <AccountUtilityApp page="account" />,
})
const billingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/billing",
  component: () => <AccountUtilityApp page="billing" />,
})
const feedbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/feedback",
  component: () => <AccountUtilityApp page="feedback" />,
})
const supportRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/support",
  component: () => <AccountUtilityApp page="support" />,
})
const adminSupportRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/support",
  component: AdminSupportApp,
})
const adminSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/settings",
  component: AdminSettingsApp,
})
const adminUsersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/users",
  component: AdminUsersApp,
})
const adminAuditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/audit",
  component: AdminAuditApp,
})
const pathRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/path",
  component: () => <LearnerApp page="path" />,
})
const activityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/activity",
  beforeLoad: () => {
    throw redirect({ to: "/", hash: "recent-activity" })
  },
})
const analyticsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/analytics",
  component: () => <LearnerApp page="analytics" />,
})
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: Login,
})
const consentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/consent",
  component: Consent,
})
const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/register",
  component: Registration,
})
const verifyEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/verify-email",
  component: VerifyEmail,
})
const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/forgot-password",
  component: ForgotPassword,
})
const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reset-password",
  component: ResetPassword,
})
const onboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/onboarding",
  component: Onboarding,
})
const guardianVerifyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/guardian/verify",
  component: GuardianVerify,
})
const routeTree = rootRoute.addChildren([
  overviewRoute,
  masteryRoute,
  accountRoute,
  billingRoute,
  feedbackRoute,
  supportRoute,
  adminSupportRoute,
  adminSettingsRoute,
  adminUsersRoute,
  adminAuditRoute,
  pathRoute,
  activityRoute,
  analyticsRoute,
  loginRoute,
  consentRoute,
  registerRoute,
  verifyEmailRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  onboardingRoute,
  guardianVerifyRoute,
])

export function createAppRouter() {
  return createRouter({ routeTree, defaultPreload: "intent" })
}

export const router = createAppRouter()

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router
  }
}

type AccountUtilityPage = "account" | "billing" | "feedback" | "support"
type LearnerContextPage = Exclude<LearnerPage, AccountUtilityPage>

export function AccountUtilityApp({
  onLoginRequired = redirectToLogin,
  page,
}: {
  onLoginRequired?: (returnTo: string) => void
  page: AccountUtilityPage
}) {
  const { t } = useTranslation()
  const session = useQuery({
    queryKey: ["auth", "session"],
    queryFn: getWebSession,
    staleTime: 60_000,
    retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 2,
  })
  const billingStatus = useQuery({
    ...billingStatusQueryOptions(),
    enabled: page === "billing" && Boolean(session.data),
  })
  const accountContext = useQuery({
    queryKey: ["learner", "me", "account-optional"],
    queryFn: getLearnerMe,
    enabled: page === "account" && Boolean(session.data),
    staleTime: 60_000,
    retry: false,
  })
  const loginRequired =
    (page !== "support" && session.data === null) ||
    (page !== "support" && session.error instanceof ApiError && session.error.status === 401) ||
    (page === "billing" &&
      billingStatus.error instanceof ApiError &&
      billingStatus.error.status === 401)

  useEffect(() => {
    if (loginRequired) onLoginRequired(`/${page}`)
  }, [loginRequired, onLoginRequired, page])

  if (session.isPending || loginRequired || (page === "billing" && billingStatus.isPending)) {
    return <FullPageStatus label={t("accountUtility.loadingSession")} />
  }
  if (page !== "support" && (session.isError || !session.data)) {
    return <FullPageError retry={() => session.refetch()} />
  }
  if (page === "support" && !session.data) {
    return (
      <div className="page-wrap">
        <Suspense fallback={<DashboardSkeleton />}>
          <SupportPage session={null} />
        </Suspense>
      </div>
    )
  }
  const activeSession = session.data
  if (!activeSession) return <FullPageError retry={() => session.refetch()} />
  return (
    <LearnerShell guardianOnly={false} name={activeSession.user.name} page={page}>
      <Helmet>
        <title>{t(`nav.${page}`)} · TutorLite</title>
      </Helmet>
      <div className="page-rail">
        <Suspense fallback={<DashboardSkeleton />}>
          {page === "account" ? (
            <AccountPage
              changeLanguage={updateLocale}
              {...(accountContext.data ? { me: accountContext.data } : {})}
              session={activeSession}
              onSignOut={signOut}
            />
          ) : page === "billing" ? (
            <BillingPage onUnauthorized={() => onLoginRequired("/billing")} />
          ) : page === "support" ? (
            <SupportPage session={activeSession} />
          ) : (
            <FeedbackPage />
          )}
        </Suspense>
      </div>
    </LearnerShell>
  )
}

function AdminSupportApp() {
  return (
    <Suspense fallback={<DashboardSkeleton />}>
      <AdminSupportPage />
    </Suspense>
  )
}

function AdminSettingsApp() {
  return (
    <Suspense fallback={<DashboardSkeleton />}>
      <AdminSettingsPage />
    </Suspense>
  )
}

function AdminUsersApp() {
  return (
    <Suspense fallback={<DashboardSkeleton />}>
      <AdminUsersPage />
    </Suspense>
  )
}

function AdminAuditApp() {
  return (
    <Suspense fallback={<DashboardSkeleton />}>
      <AdminAuditPage />
    </Suspense>
  )
}

function redirectToLogin(returnTo: string) {
  window.location.assign(`/login?returnTo=${encodeURIComponent(returnTo)}`)
}

export function LearnerApp({ page }: { page: LearnerContextPage }) {
  const { i18n, t } = useTranslation()
  const me = useQuery({
    queryKey: ["learner", "me"],
    queryFn: getLearnerMe,
    staleTime: 60_000,
    retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 2,
  })
  const access = me.data ? resolveLearnerAccess(me.data, page) : undefined
  const mastery = useQuery({
    queryKey: ["learner", "mastery", me.data?.owner.locale],
    queryFn: getMastery,
    enabled: (page === "home" || page === "mastery") && access?.status === "ready",
    staleTime: 30_000,
    retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 2,
  })

  useEffect(() => {
    const error = me.error
    if (error instanceof ApiError && error.status === 401) {
      window.location.assign(`/login?returnTo=${encodeURIComponent(window.location.pathname)}`)
    }
  }, [me.error])

  useEffect(() => {
    const savedLocale = me.data?.owner.locale
    if (!savedLocale || i18n.language.startsWith(savedLocale)) return
    localStorage.setItem("tl_locale", savedLocale)
    document.documentElement.lang = savedLocale
    void i18n.changeLanguage(savedLocale)
  }, [i18n, me.data?.owner.locale])

  if (me.isPending) return <FullPageStatus label={t("loading")} />
  if (me.isError || !me.data) return <FullPageError retry={() => me.refetch()} />

  return (
    <LearnerShell
      guardianOnly={me.data.accountContext.guardianOnly}
      name={me.data.user.name}
      page={page}
    >
      <Helmet>
        <title>{t(`nav.${page}`)} · TutorLite</title>
      </Helmet>
      <div className={page === "home" ? "page-rail page-rail-home" : "page-rail"}>
        {access && access.status !== "ready" ? (
          <PageAccess access={access} />
        ) : page === "path" ? (
          <Suspense fallback={<DashboardSkeleton />}>
            <PathPage />
          </Suspense>
        ) : page === "analytics" ? (
          <Suspense fallback={<DashboardSkeleton />}>
            <AnalyticsPage />
          </Suspense>
        ) : page === "mastery" ? (
          <MasteryPage query={mastery} />
        ) : (
          <Overview name={me.data.user.name} query={mastery} />
        )}
      </div>
    </LearnerShell>
  )
}

function AccountPage({
  changeLanguage,
  me,
  onSignOut,
  session,
}: {
  changeLanguage: (language: "en" | "id") => Promise<void>
  me?: LearnerMe
  onSignOut: () => Promise<void>
  session?: NonNullable<WebSession>
}) {
  const { i18n, t } = useTranslation()
  const queryClient = useQueryClient()
  const accountName = me?.user.name ?? session?.user.name ?? t("nav.account")
  const accountEmail = me?.user.email ?? session?.user.email ?? ""
  const initialLocale = me?.owner.locale ?? (i18n.language.startsWith("en") ? "en" : "id")
  const [locale, setLocale] = useState<"en" | "id">(initialLocale)
  const [localeStatus, setLocaleStatus] = useState<"error" | "idle" | "pending" | "saved">("idle")
  const [signOutPending, setSignOutPending] = useState(false)
  const [signOutError, setSignOutError] = useState(false)
  const [exportStatus, setExportStatus] = useState<
    "aborted" | "error" | "fresh_required" | "idle" | "pending" | "success"
  >("idle")
  const [exportBytes, setExportBytes] = useState(0)
  const [exportAbort, setExportAbort] = useState<AbortController | null>(null)
  const [deleteTyped, setDeleteTyped] = useState("")
  const [deleteAcknowledged, setDeleteAcknowledged] = useState(false)
  const [deleteDisposition, setDeleteDisposition] = useState<
    "forfeiture_selection" | "refund_review" | undefined
  >(undefined)
  const [deleteIdempotencyKey] = useState(() => crypto.randomUUID())
  const [cancelIdempotencyKey, setCancelIdempotencyKey] = useState(() => crypto.randomUUID())
  const [proofEmail, setProofEmail] = useState(accountEmail)
  const [proofChallengeToken, setProofChallengeToken] = useState("")
  const [proofToken, setProofToken] = useState("")
  const clients = useInfiniteQuery({
    queryKey: ["account", "authorized-clients"],
    queryFn: ({ pageParam }) => getAuthorizedClients(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.cursor,
    staleTime: 15_000,
  })
  const clientItems = clients.data?.pages.flatMap((page) => page.clients) ?? []
  const guardians = useQuery({
    queryKey: ["account", "guardian"],
    queryFn: getGuardianRelationships,
    staleTime: 15_000,
    retry: false,
  })
  const linkedProviders = useQuery({
    queryKey: ["account", "linked-providers"],
    queryFn: getLinkedProviders,
    staleTime: 15_000,
    retry: false,
  })
  const providerLinkParams = new URLSearchParams(window.location.search)
  const callbackAttemptId = providerLinkParams.get("providerLinkAttempt")
  const callbackAttemptError = providerLinkParams.get("error")
  const activeAttemptId = callbackAttemptId ?? linkedProviders.data?.activeAttempt?.attemptId
  const linkStatus = useQuery({
    queryKey: ["account", "linked-providers", "status", activeAttemptId, callbackAttemptError],
    queryFn: () => getAccountProviderLinkStatus(activeAttemptId ?? "", callbackAttemptError),
    enabled: Boolean(activeAttemptId),
    retry: false,
  })
  const startProviderLink = useMutation({
    mutationFn: startAccountProviderLink,
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ["account", "linked-providers"] })
      if (result.authorizationUrl) window.location.assign(result.authorizationUrl)
    },
  })
  const unlinkProvider = useMutation({
    mutationFn: unlinkAccountProvider,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["account", "linked-providers"] }),
  })
  const revoke = useMutation({
    mutationFn: revokeAuthorizedClient,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["account", "authorized-clients"] }),
  })
  const guardianRevoke = useMutation({
    mutationFn: ({ relationshipId, purposes }: { relationshipId: string; purposes: string[] }) =>
      revokeGuardianConsent(relationshipId, purposes),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["account", "guardian"] }),
  })
  const deletionPreview = useQuery({
    queryKey: ["account", "deletion", "preview", locale],
    queryFn: () => getAccountDeletionPreview(locale),
    staleTime: 0,
    retry: false,
  })
  const deletionStatus = useQuery({
    queryKey: ["account", "deletion", "status", proofToken],
    queryFn: () => getAccountDeletionStatus(proofToken || undefined),
    staleTime: 0,
    retry: false,
  })
  const deletionRequest = useMutation({
    mutationFn: requestAccountDeletion,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["account", "deletion"] })
    },
  })
  const deletionCancel = useMutation({
    mutationFn: () =>
      cancelAccountDeletion(
        {
          ...(deletionStatus.data?.request?.requestId
            ? { requestId: deletionStatus.data.request.requestId }
            : {}),
          idempotencyKey: cancelIdempotencyKey,
        },
        proofToken || undefined,
      ),
    onSuccess: async () => {
      setCancelIdempotencyKey(crypto.randomUUID())
      await queryClient.invalidateQueries({ queryKey: ["account", "deletion"] })
    },
  })
  const deletionProofRequest = useMutation({
    mutationFn: requestDeletionStatusChallenge,
  })
  const deletionProofVerify = useMutation({
    mutationFn: verifyDeletionStatusChallenge,
    onSuccess: async (result) => {
      setProofToken(result.proofToken)
      await queryClient.invalidateQueries({ queryKey: ["account", "deletion", "status"] })
    },
  })

  async function saveLocale() {
    setLocaleStatus("pending")
    try {
      await changeLanguage(locale)
      await queryClient.invalidateQueries({ queryKey: ["learner"] })
      setLocaleStatus("saved")
    } catch {
      setLocaleStatus("error")
    }
  }

  async function logout() {
    setSignOutPending(true)
    setSignOutError(false)
    try {
      await onSignOut()
      queryClient.clear()
      window.location.assign("/login")
    } catch {
      setSignOutError(true)
    } finally {
      setSignOutPending(false)
    }
  }

  async function startExport() {
    const controller = new AbortController()
    setExportAbort(controller)
    setExportBytes(0)
    setExportStatus("pending")
    try {
      const result = await exportAccountData({
        signal: controller.signal,
        onProgress: ({ bytesReceived }) => setExportBytes(bytesReceived),
      })
      const url = URL.createObjectURL(result.blob)
      const link = document.createElement("a")
      link.href = url
      link.download = result.filename
      document.body.append(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
      setExportBytes(result.bytesReceived)
      setExportStatus("success")
    } catch (error) {
      setExportStatus(
        controller.signal.aborted
          ? "aborted"
          : error instanceof ApiError && error.code === "fresh_session_required"
            ? "fresh_required"
            : "error",
      )
    } finally {
      setExportAbort(null)
    }
  }

  function abortExport() {
    exportAbort?.abort()
    setExportStatus("aborted")
  }

  return (
    <>
      <section className="section-heading">
        <h1>{t("nav.account")}</h1>
        <p>{t("accountDetail")}</p>
      </section>
      <section className="account-stack">
        <Card>
          <CardHeader>
            <UserRound className="section-icon" aria-hidden="true" />
            <CardTitle>{t("accountSections.profile")}</CardTitle>
            <CardDescription>{accountEmail}</CardDescription>
          </CardHeader>
          <CardContent>
            <h2>{accountName}</h2>
            {me && !me.accountContext.guardianOnly ? (
              <dl className="detail-list">
                <div>
                  <dt>{t("accountSections.enrollment")}</dt>
                  <dd>
                    <Badge variant="secondary">{me.enrollment.status}</Badge>
                  </dd>
                </div>
              </dl>
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <DownloadCloud className="section-icon" aria-hidden="true" />
            <CardTitle>{t("accountSections.exportTitle")}</CardTitle>
            <CardDescription>{t("accountSections.exportDescription")}</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="muted-copy" aria-live="polite">
              {exportStatus === "pending"
                ? t("accountSections.exportProgress", {
                    bytes: new Intl.NumberFormat().format(exportBytes),
                  })
                : exportStatus === "success"
                  ? t("accountSections.exportSuccess", {
                      bytes: new Intl.NumberFormat().format(exportBytes),
                    })
                  : exportStatus === "aborted"
                    ? t("accountSections.exportAborted")
                    : exportStatus === "fresh_required"
                      ? t("accountSections.exportFreshRequired")
                      : exportStatus === "error"
                        ? t("accountSections.exportError")
                        : t("accountSections.exportIdle")}
            </p>
          </CardContent>
          <CardFooter className="button-row">
            <Button disabled={exportStatus === "pending"} onClick={() => void startExport()}>
              {exportStatus === "pending"
                ? t("accountSections.exportPreparing")
                : t("accountSections.exportAction")}
            </Button>
            {exportStatus === "pending" ? (
              <Button variant="secondary" onClick={abortExport}>
                {t("accountSections.exportAbort")}
              </Button>
            ) : null}
          </CardFooter>
        </Card>

        {me?.accountContext.guardianOnly ? (
          <Card>
            <CardHeader>
              <GraduationCap className="section-icon" aria-hidden="true" />
              <CardTitle>{t("accountSections.optionalLearnerTitle")}</CardTitle>
              <CardDescription>{t("accountSections.optionalLearnerDescription")}</CardDescription>
            </CardHeader>
            <CardFooter>
              <Button asChild variant="secondary">
                <a href="/onboarding?setup=learner">{t("accountSections.optionalLearnerAction")}</a>
              </Button>
            </CardFooter>
          </Card>
        ) : null}

        {me ? (
          <Card>
            <CardHeader>
              <Languages className="section-icon" aria-hidden="true" />
              <CardTitle>{t("accountSections.language")}</CardTitle>
              <CardDescription>{t("accountSections.languageDescription")}</CardDescription>
            </CardHeader>
            <CardContent>
              <Field>
                <FieldLabel htmlFor="account-locale">{t("language")}</FieldLabel>
                <Select value={locale} onValueChange={(value) => setLocale(value as "en" | "id")}>
                  <SelectTrigger id="account-locale">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="id">Bahasa Indonesia</SelectItem>
                      <SelectItem value="en">English</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <FieldDescription aria-live="polite">
                  {localeStatus === "saved"
                    ? t("accountSections.languageSaved")
                    : localeStatus === "error"
                      ? t("accountSections.languageError")
                      : t("accountSections.languageHint")}
                </FieldDescription>
              </Field>
            </CardContent>
            <CardFooter>
              <Button
                disabled={localeStatus === "pending" || locale === me.owner.locale}
                onClick={() => void saveLocale()}
              >
                {localeStatus === "pending" ? t("common.saving") : t("common.save")}
              </Button>
            </CardFooter>
          </Card>
        ) : null}

        {me ? (
          <Card>
            <CardHeader>
              <ShieldCheck className="section-icon" aria-hidden="true" />
              <CardTitle>{t("accountSections.guardianSection")}</CardTitle>
              <CardDescription>{t("accountSections.guardianDescription")}</CardDescription>
            </CardHeader>
            <CardContent>
              {guardians.isPending ? <DashboardSkeleton /> : null}
              {guardians.isError ? <InlineError retry={() => guardians.refetch()} /> : null}
              {guardians.data?.relationships.length === 0 ? (
                <p className="muted-copy">{t("accountSections.noGuardian")}</p>
              ) : null}
              <div className="guardian-relationships">
                {guardians.data?.relationships.map((relationship) => (
                  <GuardianRelationshipPanel
                    key={relationship.relationshipId}
                    relationship={relationship}
                    pending={guardianRevoke.isPending}
                    revoke={(purpose) =>
                      guardianRevoke.mutate({
                        relationshipId: relationship.relationshipId,
                        purposes: [purpose],
                      })
                    }
                  />
                ))}
              </div>
              {guardianRevoke.isError ? (
                <p role="alert">{t("accountSections.guardianRevokeError")}</p>
              ) : null}
            </CardContent>
          </Card>
        ) : null}

        <Card>
          <CardHeader>
            <Trash2 className="section-icon" aria-hidden="true" />
            <CardTitle>{t("accountSections.deleteTitle")}</CardTitle>
            <CardDescription>{t("accountSections.deleteDescription")}</CardDescription>
          </CardHeader>
          <CardContent className="feedback-fields">
            {deletionPreview.isPending ? <DashboardSkeleton /> : null}
            {deletionPreview.isError ? (
              <InlineError retry={() => deletionPreview.refetch()} />
            ) : null}
            {deletionPreview.data ? (
              <div className="form-notice" role="status">
                <p>{deletionPreview.data.copy.summary}</p>
                <p>
                  {t("accountSections.deleteValueFacts", {
                    passes:
                      deletionPreview.data.valueFacts.activeEntitlementCount +
                      deletionPreview.data.valueFacts.scheduledEntitlementCount,
                    credits: deletionPreview.data.valueFacts.remainingCredits,
                  })}
                </p>
                {deletionPreview.data.blockers.length ? (
                  <ul className="history-list">
                    {deletionPreview.data.blockers.map((blocker) => (
                      <li key={blocker.code}>
                        <strong>{blocker.title ?? blocker.code}</strong>
                        <span>{blocker.body ?? blocker.code}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>{t("accountSections.deleteNoBlockers")}</p>
                )}
              </div>
            ) : null}
            {deletionStatus.data?.request ? (
              <div className="form-notice" role="status">
                <p>
                  {t("accountSections.deleteStatus", {
                    status: deletionStatus.data.request.status,
                  })}
                </p>
                <p>{t("accountSections.deleteNoRestorePromise")}</p>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={!deletionStatus.data.request.canCancel || deletionCancel.isPending}
                  onClick={() => deletionCancel.mutate()}
                >
                  {deletionCancel.isPending
                    ? t("accountSections.deleteCancelling")
                    : t("accountSections.deleteCancel")}
                </Button>
              </div>
            ) : null}
            <Field>
              <FieldLabel htmlFor="delete-disposition">
                {t("accountSections.deleteValueDisposition")}
              </FieldLabel>
              <Select
                {...(deleteDisposition ? { value: deleteDisposition } : {})}
                onValueChange={(value) =>
                  setDeleteDisposition(value as "forfeiture_selection" | "refund_review")
                }
              >
                <SelectTrigger id="delete-disposition">
                  <SelectValue placeholder={t("accountSections.deleteDispositionPlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="refund_review">
                      {t("accountSections.deleteRefundReview")}
                    </SelectItem>
                    <SelectItem value="forfeiture_selection">
                      {t("accountSections.deleteForfeit")}
                    </SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
              <FieldDescription>{t("accountSections.deleteDispositionHint")}</FieldDescription>
            </Field>
            <label className="checkbox-row" htmlFor="delete-acknowledge">
              <Checkbox
                id="delete-acknowledge"
                checked={deleteAcknowledged}
                onCheckedChange={(checked) => setDeleteAcknowledged(checked === true)}
              />
              {t("accountSections.deleteAcknowledge")}
            </label>
            <Field>
              <FieldLabel htmlFor="delete-typed">{t("accountSections.deleteTyped")}</FieldLabel>
              <Input
                id="delete-typed"
                value={deleteTyped}
                onChange={(event) => setDeleteTyped(event.target.value)}
              />
              <FieldDescription>{t("accountSections.deleteTypedHint")}</FieldDescription>
            </Field>
            <Button
              type="button"
              disabled={
                !deletionPreview.data?.canRequest ||
                deleteTyped !== "DELETE" ||
                !deleteAcknowledged ||
                deletionRequest.isPending ||
                (deletionPreview.data?.valueFacts.unusedPaidValue && !deleteDisposition)
              }
              onClick={() =>
                deletionPreview.data &&
                deletionRequest.mutate({
                  expectedPreviewHash: deletionPreview.data.previewHash,
                  idempotencyKey: deleteIdempotencyKey,
                  ...(deleteDisposition ? { valueDisposition: deleteDisposition } : {}),
                  confirmation: {
                    typed: "DELETE",
                    exportPromptAcknowledged: true,
                    immediateDeactivationAcknowledged: true,
                  },
                })
              }
            >
              {deletionRequest.isPending
                ? t("accountSections.deleteRequesting")
                : t("accountSections.deleteRequest")}
            </Button>
            {deletionRequest.isSuccess ? (
              <p className="muted-copy">{t("accountSections.deleteRequested")}</p>
            ) : null}
            {deletionRequest.isError ? (
              <p role="alert">
                {t(`accountSections.deleteErrors.${apiErrorCode(deletionRequest.error)}`, {
                  defaultValue: t("accountSections.deleteErrors.default"),
                })}
              </p>
            ) : null}
            {deletionCancel.isError ? (
              <p role="alert">{t("accountSections.deleteCancelError")}</p>
            ) : null}
            <Separator />
            <FieldSet>
              <FieldLegend>{t("accountSections.deleteProofTitle")}</FieldLegend>
              <FieldDescription>{t("accountSections.deleteProofDescription")}</FieldDescription>
              <Field>
                <FieldLabel htmlFor="delete-proof-email">{t("support.email")}</FieldLabel>
                <Input
                  id="delete-proof-email"
                  type="email"
                  value={proofEmail}
                  onChange={(event) => setProofEmail(event.target.value)}
                />
              </Field>
              <Button
                type="button"
                variant="secondary"
                disabled={!proofEmail || deletionProofRequest.isPending}
                onClick={() => deletionProofRequest.mutate({ email: proofEmail, locale })}
              >
                {t("accountSections.deleteProofSend")}
              </Button>
              <Field>
                <FieldLabel htmlFor="delete-proof-token">
                  {t("accountSections.deleteProofToken")}
                </FieldLabel>
                <Input
                  id="delete-proof-token"
                  value={proofChallengeToken}
                  onChange={(event) => setProofChallengeToken(event.target.value)}
                />
              </Field>
              <Button
                type="button"
                variant="secondary"
                disabled={!proofEmail || !proofChallengeToken || deletionProofVerify.isPending}
                onClick={() =>
                  deletionProofVerify.mutate({ email: proofEmail, token: proofChallengeToken })
                }
              >
                {t("accountSections.deleteProofVerify")}
              </Button>
              {proofToken ? (
                <p className="muted-copy">{t("accountSections.deleteProofReady")}</p>
              ) : null}
            </FieldSet>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <KeyRound className="section-icon" aria-hidden="true" />
            <CardTitle>{t("accountSections.loginMethods")}</CardTitle>
            <CardDescription>{t("accountSections.loginMethodsDescription")}</CardDescription>
          </CardHeader>
          <CardContent>
            {linkedProviders.isPending ? <DashboardSkeleton /> : null}
            {linkedProviders.isError ? (
              <InlineError retry={() => linkedProviders.refetch()} />
            ) : null}
            {linkedProviders.data?.providers.length === 0 ? (
              <p className="muted-copy">{t("accountSections.noLoginMethods")}</p>
            ) : null}
            <ul className="client-list">
              {linkedProviders.data?.providers.map((provider) => (
                <li key={provider.accountRowId}>
                  <div>
                    <strong>{provider.label}</strong>
                    <span>
                      {t(`accountSections.providerKind.${provider.kind}`)}
                      {provider.lastUsableMethod
                        ? ` · ${t("accountSections.lastLoginMethod")}`
                        : ""}
                    </span>
                  </div>
                  <Button
                    variant="secondary"
                    disabled={!provider.canUnlink || unlinkProvider.isPending}
                    type="button"
                    onClick={() => unlinkProvider.mutate({ accountRowId: provider.accountRowId })}
                  >
                    {t("accountSections.unlinkProvider")}
                  </Button>
                </li>
              ))}
            </ul>
            {linkedProviders.data?.linkableProviders.map((provider) => (
              <Button
                key={provider.provider}
                type="button"
                disabled={startProviderLink.isPending}
                onClick={() => startProviderLink.mutate({ provider: provider.provider })}
              >
                {startProviderLink.isPending
                  ? t("accountSections.providerLinkStarting")
                  : t("accountSections.linkProvider", { provider: provider.label })}
              </Button>
            ))}
            {linkedProviders.data?.activeAttempt ? (
              <p className="muted-copy">
                {t("accountSections.providerLinkPending", {
                  provider: linkedProviders.data.activeAttempt.provider,
                })}
              </p>
            ) : null}
            {linkStatus.data ? (
              <div className="form-notice" role="status">
                <p>{t(`accountSections.providerLinkStatus.${linkStatus.data.status}`)}</p>
                {linkStatus.data.warnings.learningHistoryDoesNotTransfer ? (
                  <p>{t("accountSections.providerLinkLearningWarning")}</p>
                ) : null}
                {linkStatus.data.warnings.billingValueDoesNotTransfer ? (
                  <p>{t("accountSections.providerLinkBillingWarning")}</p>
                ) : null}
                {linkStatus.data.status === "manual_support_required" ? (
                  <Button asChild variant="secondary">
                    <a href="/support">
                      {t("accountSections.providerLinkSupport", {
                        attemptId: linkStatus.data.attemptId,
                      })}
                    </a>
                  </Button>
                ) : null}
              </div>
            ) : null}
            {startProviderLink.isError ? (
              <p role="alert">{t("accountSections.providerLinkError")}</p>
            ) : null}
            {unlinkProvider.isError ? (
              <p role="alert">{t("accountSections.providerUnlinkError")}</p>
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <PlugZap className="section-icon" aria-hidden="true" />
            <CardTitle>{t("connectedClients")}</CardTitle>
            <CardDescription>{t("accountSections.clientsDescription")}</CardDescription>
          </CardHeader>
          <CardContent>
            {clients.isPending ? <DashboardSkeleton /> : null}
            {clients.isError ? <InlineError retry={() => clients.refetch()} /> : null}
            {clientItems.length === 0 ? <p className="muted-copy">{t("noClients")}</p> : null}
            <ul className="client-list">
              {clientItems.map((client) => (
                <li key={client.grantId}>
                  <div>
                    <strong>{safeClientLabel(client.clientId)}</strong>
                    <span>{client.scopes.join(" · ")}</span>
                  </div>
                  <Button
                    variant="secondary"
                    disabled={revoke.isPending}
                    type="button"
                    onClick={() => revoke.mutate(client.grantId)}
                  >
                    {t("revoke")}
                  </Button>
                </li>
              ))}
            </ul>
            {clients.hasNextPage ? (
              <Button
                variant="secondary"
                disabled={clients.isFetchingNextPage}
                onClick={() => void clients.fetchNextPage()}
              >
                {clients.isFetchingNextPage
                  ? t("accountSections.loadingMoreClients")
                  : t("accountSections.moreClients")}
              </Button>
            ) : null}
            {revoke.isError ? <p role="alert">{t("accountSections.revokeError")}</p> : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <LogOut className="section-icon" aria-hidden="true" />
            <CardTitle>{t("accountSections.session")}</CardTitle>
            <CardDescription>{t("accountSections.sessionDescription")}</CardDescription>
          </CardHeader>
          <CardContent>
            <Separator />
            {signOutError ? <p role="alert">{t("accountSections.signOutError")}</p> : null}
          </CardContent>
          <CardFooter>
            <Button variant="outline" disabled={signOutPending} onClick={() => void logout()}>
              <LogOut data-icon="inline-start" aria-hidden="true" />
              {signOutPending ? t("accountSections.signingOut") : t("signOut")}
            </Button>
          </CardFooter>
        </Card>
      </section>
    </>
  )
}

function safeClientLabel(clientId: string) {
  try {
    const host = new URL(clientId).hostname.replace(/^www\./, "")
    if (host.includes("chatgpt")) return "ChatGPT"
    if (host.includes("claude")) return "Claude"
    if (host.includes("cursor")) return "Cursor"
    return host
  } catch {
    return "MCP client"
  }
}

function apiErrorCode(error: unknown) {
  return error instanceof ApiError ? error.code : "default"
}

function GuardianRelationshipPanel({
  pending,
  relationship,
  revoke,
}: {
  pending: boolean
  relationship: GuardianRelationship
  revoke: (purpose: string) => void
}) {
  const { t } = useTranslation()
  return (
    <section className="guardian-relationship">
      <header>
        <div>
          <strong>{t(`accountSections.guardianRole.${relationship.role}`)}</strong>
          <p>{t(`accountSections.guardianStatus.${relationship.status}`)}</p>
        </div>
        <Badge variant={relationship.consentStatus === "active" ? "default" : "secondary"}>
          {relationship.consentStatus
            ? t(`accountSections.consentStatus.${relationship.consentStatus}`)
            : t("common.pending")}
        </Badge>
      </header>
      {relationship.purposes.length ? (
        <ul className="guardian-purpose-list">
          {relationship.purposes.map((purpose) => (
            <li key={purpose}>
              <span>{t(`guardianPurposes.${purpose}`)}</span>
              {relationship.role === "guardian" ? (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={pending}
                  onClick={() => revoke(purpose)}
                >
                  {t("accountSections.revokePurpose")}
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      {relationship.role === "guardian" && relationship.purposes.includes("guardian_summary") ? (
        <GuardianSummary relationshipId={relationship.relationshipId} />
      ) : null}
      {relationship.role === "learner" &&
      !["progress", "content_practice", "cross_border", "oauth_mcp"].every((purpose) =>
        relationship.purposes.includes(purpose),
      ) ? (
        <Button asChild className="mt-4" variant="secondary">
          <a href="/onboarding">{t("accountSections.requestGuardianReview")}</a>
        </Button>
      ) : null}
    </section>
  )
}

function GuardianSummary({ relationshipId }: { relationshipId: string }) {
  const { t } = useTranslation()
  const summary = useQuery({
    queryKey: ["account", "guardian", relationshipId, "summary"],
    queryFn: () => getGuardianSummary(relationshipId),
    retry: false,
  })
  if (summary.isPending) return <DashboardSkeleton />
  if (summary.isError) return <p role="alert">{t("accountSections.summaryError")}</p>
  return (
    <dl className="guardian-summary">
      <div>
        <dt>{t("accountSections.summaryInteractions")}</dt>
        <dd>{summary.data.interactionCount}</dd>
      </div>
      <div>
        <dt>{t("accountSections.summaryNextTopic")}</dt>
        <dd>{summary.data.nextTopic ?? t("common.unavailable")}</dd>
      </div>
      <div>
        <dt>{t("accountSections.summaryLastActivity")}</dt>
        <dd>
          {summary.data.lastLearningAt
            ? new Date(summary.data.lastLearningAt).toLocaleDateString()
            : t("common.unavailable")}
        </dd>
      </div>
    </dl>
  )
}

type MasteryQuery = UseQueryResult<Mastery, Error>

function Overview({ name, query }: { name: string; query: MasteryQuery }) {
  const { t } = useTranslation()
  const path = useQuery({ queryKey: ["learner", "path"], queryFn: getLearningPath, retry: false })
  const analytics = useQuery({
    queryKey: ["learner", "analytics"],
    queryFn: getLearningAnalytics,
    retry: false,
  })
  if (query.isPending) return <DashboardSkeleton />
  if (query.isError) return <InlineError retry={() => query.refetch()} />
  const items = query.data?.items ?? []
  const states = ["unseen", "learning", "practicing", "mastered"] as const
  const nextTopic =
    path.data?.path?.items[0]?.topicName ??
    items.find((item) => item.state !== "mastered")?.topicName ??
    items[0]?.topicName
  return (
    <>
      <section className="overview-hero">
        <div className="hero-copy">
          <p className="eyebrow">{t("greeting", { name: greetingName(name) })}</p>
          <h1>{t("heading")}</h1>
          <p>{t("intro")}</p>
        </div>
        <LearningCompanion />
      </section>
      <section className="trail-map" aria-label={t("progressSummary")}>
        <div className="trail-map-heading">
          <p className="eyebrow">{t("trailMap")}</p>
          <p>{t("trailHint")}</p>
        </div>
        <svg
          className="trail-route trail-route-desktop"
          viewBox="0 0 1000 150"
          preserveAspectRatio="none"
          aria-hidden="true"
          focusable="false"
        >
          <path d="M125 75 C210 18 290 132 375 75 S540 18 625 75 S790 132 875 75" />
        </svg>
        <svg
          className="trail-route trail-route-mobile"
          viewBox="0 0 320 330"
          preserveAspectRatio="none"
          aria-hidden="true"
          focusable="false"
        >
          <path d="M72 65 C120 24 201 25 248 65 C300 110 291 221 248 265 C200 312 121 226 72 265" />
        </svg>
        <ol className="learning-trail">
          {states.map((state, index) => (
            <li className={`trail-stop trail-${state}`} key={state}>
              <span className="trail-marker">
                <strong>{items.filter((item) => item.state === state).length}</strong>
              </span>
              <span className="trail-index" aria-hidden="true">
                0{index + 1}
              </span>
              <span className="trail-label">{t(state)}</span>
            </li>
          ))}
        </ol>
      </section>
      {items.length ? (
        <section className="paper-card next-card">
          <div>
            <p className="eyebrow">{t("nextUp")}</p>
            <h2>{nextTopic}</h2>
          </div>
          <a className="dark-pill" href="/mastery">
            {t("continue")}
          </a>
        </section>
      ) : (
        <EmptyState />
      )}
      {analytics.data ? (
        <Card>
          <CardHeader>
            <CardTitle>{t("home.summaryTitle")}</CardTitle>
            <CardDescription>{t("home.summaryDescription")}</CardDescription>
          </CardHeader>
          <CardContent className="metric-row">
            <div>
              <strong>{analytics.data.recentInteractionCount}</strong>
              <span>{t("analyticsPage.interactions")}</span>
            </div>
            <div>
              <strong>{analytics.data.currentPeriodUsage}</strong>
              <span>{t("analyticsPage.usage")}</span>
            </div>
          </CardContent>
        </Card>
      ) : null}
      <RecentActivity />
    </>
  )
}

function greetingName(name: string) {
  return name.trim().split(/\s+/u)[0] || name
}

function LearningCompanion() {
  return (
    <svg className="learning-companion" viewBox="0 0 320 250" aria-hidden="true" focusable="false">
      <path className="companion-route" d="M21 205c56-42 99 20 153-26 39-33 70-21 125-57" />
      <path
        className="companion-spark companion-spark-blue"
        d="m52 66 5 12 13 3-13 5-5 12-5-12-13-5 13-3Z"
      />
      <path
        className="companion-spark companion-spark-pink"
        d="m267 42 3 8 9 2-9 3-3 9-4-9-8-3 8-2Z"
      />
      <circle className="companion-sun" cx="250" cy="91" r="25" />
      <path
        className="companion-body"
        d="M94 80c29-26 88-17 110 15 22 31 7 87-28 108-34 21-87 6-105-29-17-33-4-70 23-94Z"
      />
      <path className="companion-page" d="M87 114c25-13 48-8 68 9v67c-20-16-43-20-68-7Z" />
      <path className="companion-page" d="M155 123c19-17 42-22 67-9v69c-25-13-48-9-67 7Z" />
      <path
        className="companion-page-line"
        d="M101 139c13-3 25-1 38 5M101 153c13-3 25-1 38 5M173 143c11-6 22-8 34-5M173 157c11-6 22-8 34-5"
      />
      <path className="companion-face" d="M129 100h1m39 0h1m-34 13c9 8 18 8 27 0" />
      <path
        className="companion-limb"
        d="M82 142c-22 8-28 22-18 40m151-39c20 5 29 16 27 34M115 202l-10 25m76-25 13 23"
      />
      <path className="companion-flag" d="M241 177v-55m0 5 37 9-37 17" />
    </svg>
  )
}

function MasteryPage({ query }: { query: MasteryQuery }) {
  const { t } = useTranslation()
  if (query.isPending) return <PageLoading />
  if (query.isError) return <PageError retry={() => query.refetch()} />
  const items = query.data?.items ?? []
  return (
    <>
      <section className="section-heading">
        <h1>{t("nav.mastery")}</h1>
        <p>{t("masteryPage.description")}</p>
      </section>
      {items.length ? (
        <section className="topic-grid">
          {items.map((item) => (
            <TopicCard item={item} key={item.topicId} />
          ))}
        </section>
      ) : (
        <PageEmpty
          title={t("masteryPage.emptyTitle")}
          description={t("masteryPage.emptyBody")}
          action="/path"
          actionLabel={t("masteryPage.emptyAction")}
        />
      )}
    </>
  )
}

function TopicCard({ item }: { item: MasteryItem }) {
  const { t } = useTranslation()
  return (
    <Card className={`mastery-card topic-card-${item.state}`}>
      <CardHeader>
        <div className="topic-meta">
          <Badge className={`state-${item.state}`} variant="secondary">
            {t(item.state)}
          </Badge>
          <span>{t(`difficulty.${item.difficulty}`)}</span>
        </div>
        <CardDescription>{item.collectionName}</CardDescription>
        <CardTitle>{item.topicName}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="score-row">
          <Progress
            value={item.score}
            aria-label={`${item.topicName} ${item.score}%`}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={item.score}
          />
          <strong>{item.score}%</strong>
        </div>
        <p className="topic-footnote">
          {t("evidence", { count: item.evidenceCount })} ·{" "}
          {item.updatedAt
            ? t("lastUpdated", { date: format(new Date(item.updatedAt), "d MMM yyyy") })
            : t("neverUpdated")}
        </p>
      </CardContent>
    </Card>
  )
}

function Login() {
  const { t } = useTranslation()
  const flow = new URLSearchParams(window.location.search).get("flow")
  const requestedReturn = new URLSearchParams(window.location.search).get("returnTo")
  const returnTo = safeReturnTo(requestedReturn)
  const [error, setError] = useState<string>()
  const [pending, setPending] = useState(false)
  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setPending(true)
    setError(undefined)
    try {
      const data = new FormData(event.currentTarget)
      const response = await fetch(`${apiOrigin}/api/auth/sign-in/email`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: data.get("email"), password: data.get("password") }),
      })
      if (!response.ok) return setError(t("auth.loginInvalid"))
      sessionStorage.removeItem("tl_auth_return")
      if (flow) {
        window.location.assign(`/consent?flow=${encodeURIComponent(flow)}`)
        return
      }
      if (returnTo === "/") {
        const context = await getLearnerMe().catch(() => undefined)
        if (context?.accountContext.guardianOnly) {
          window.location.assign(
            context.accountContext.currentTermsAccepted
              ? "/account"
              : "/onboarding?setup=guardian_only",
          )
          return
        }
        if (context && !context.accountContext.currentTermsAccepted) {
          window.location.assign("/onboarding")
          return
        }
        if (context?.owner.status === "pending" || context?.enrollment.status === "pending") {
          window.location.assign("/onboarding")
          return
        }
      }
      window.location.assign(returnTo)
    } catch {
      setError(t("auth.loginUnavailable"))
    } finally {
      setPending(false)
    }
  }
  return (
    <AuthFrame title={t("auth.loginTitle")}>
      <form className="auth-form" onSubmit={submit}>
        <Field>
          <FieldLabel htmlFor="login-email">{t("auth.email")}</FieldLabel>
          <Input id="login-email" name="email" type="email" autoComplete="email" required />
        </Field>
        <Field>
          <FieldLabel htmlFor="login-password">{t("auth.password")}</FieldLabel>
          <Input
            id="login-password"
            name="password"
            type="password"
            autoComplete="current-password"
            required
          />
        </Field>
        <Button disabled={pending} type="submit">
          {pending ? t("auth.signingIn") : t("auth.signIn")}
        </Button>
        {error ? (
          <p className="error-text" role="alert">
            {error}
          </p>
        ) : null}
      </form>
      <div className="auth-links">
        <a href={registrationUrl(flow, returnTo)}>{t("auth.createAccount")}</a>
        <a href="/forgot-password">{t("auth.forgotPassword")}</a>
      </div>
    </AuthFrame>
  )
}

export function safeReturnTo(value: string | null) {
  if (!value) return "/"
  try {
    const decoded = decodeURIComponent(value)
    if (!value.startsWith("/") || value.startsWith("//") || decoded.includes("\\")) return "/"
    const url = new URL(value, window.location.origin)
    if (url.origin !== window.location.origin) return "/"
    return `${url.pathname}${url.search}${url.hash}`
  } catch {
    return "/"
  }
}

function registrationUrl(flow: string | null, returnTo: string) {
  const params = new URLSearchParams()
  if (flow) params.set("flow", flow)
  else if (returnTo !== "/") params.set("returnTo", returnTo)
  const query = params.toString()
  return query ? `/register?${query}` : "/register"
}

function Registration() {
  const { i18n, t } = useTranslation()
  const flow = new URLSearchParams(window.location.search).get("flow")
  const returnTo = safeReturnTo(new URLSearchParams(window.location.search).get("returnTo"))
  const [state, setState] = useState<"form" | "sent">("form")
  const [error, setError] = useState<string>()
  const [pending, setPending] = useState(false)
  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setPending(true)
    setError(undefined)
    const data = new FormData(event.currentTarget)
    const email = String(data.get("email"))
    try {
      await registerAccount({
        name: String(data.get("name")),
        email,
        password: String(data.get("password")),
      })
      await sendVerificationEmail(email, i18n.language.startsWith("en") ? "en" : "id")
      sessionStorage.setItem(
        "tl_auth_return",
        flow ? `/consent?flow=${encodeURIComponent(flow)}` : returnTo,
      )
      setState("sent")
    } catch {
      setError(t("auth.registrationError"))
    } finally {
      setPending(false)
    }
  }
  return (
    <AuthFrame title={state === "sent" ? t("auth.checkEmail") : t("auth.registerTitle")}>
      {state === "sent" ? (
        <>
          <p>{t("auth.verificationSent")}</p>
          <Button asChild>
            <a href={loginReturnUrl()}>{t("auth.backToSignIn")}</a>
          </Button>
        </>
      ) : (
        <form className="auth-form" onSubmit={submit}>
          <Field>
            <FieldLabel htmlFor="register-name">{t("auth.name")}</FieldLabel>
            <Input id="register-name" name="name" autoComplete="name" required maxLength={200} />
          </Field>
          <Field>
            <FieldLabel htmlFor="register-email">{t("auth.email")}</FieldLabel>
            <Input id="register-email" name="email" type="email" autoComplete="email" required />
          </Field>
          <Field>
            <FieldLabel htmlFor="register-password">{t("auth.password")}</FieldLabel>
            <Input
              id="register-password"
              name="password"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              maxLength={128}
            />
          </Field>
          <Button disabled={pending} type="submit">
            {pending ? t("auth.creating") : t("auth.createAccount")}
          </Button>
          {error ? (
            <p className="error-text" role="alert">
              {error}
            </p>
          ) : null}
        </form>
      )}
    </AuthFrame>
  )
}

function VerifyEmail() {
  const { t } = useTranslation()
  const [token] = useState(() => readAndClearFragmentToken("tl_verify_token"))
  const verification = useQuery({
    queryKey: ["auth", "verify-email", token],
    queryFn: () => consumeVerificationToken(token ?? ""),
    enabled: Boolean(token),
    retry: false,
  })
  return (
    <AuthFrame title={verification.isSuccess ? t("auth.emailVerified") : t("auth.verifyEmail")}>
      {!token ? (
        <p className="error-text" role="alert">
          {t("auth.verificationMissing")}
        </p>
      ) : null}
      {verification.isPending && token ? <p aria-live="polite">{t("auth.verifying")}</p> : null}
      {verification.isError ? (
        <p className="error-text" role="alert">
          {t("auth.verificationInvalid")}
        </p>
      ) : null}
      {verification.isSuccess ? (
        <Button asChild>
          <a href={loginReturnUrl()} onClick={() => sessionStorage.removeItem("tl_verify_token")}>
            {t("auth.continueSignIn")}
          </a>
        </Button>
      ) : null}
    </AuthFrame>
  )
}

function ForgotPassword() {
  const { t } = useTranslation()
  const [sent, setSent] = useState(false)
  const [pending, setPending] = useState(false)
  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const data = new FormData(event.currentTarget)
    setPending(true)
    try {
      await requestPasswordReset(String(data.get("email"))).catch(() => undefined)
      setSent(true)
    } finally {
      setPending(false)
    }
  }
  return (
    <AuthFrame title={t("auth.forgotTitle")}>
      {sent ? (
        <p>{t("auth.resetSent")}</p>
      ) : (
        <form className="auth-form" onSubmit={submit}>
          <Field>
            <FieldLabel htmlFor="forgot-email">{t("auth.email")}</FieldLabel>
            <Input id="forgot-email" name="email" type="email" autoComplete="email" required />
          </Field>
          <Button disabled={pending} type="submit">
            {pending ? t("auth.sending") : t("auth.sendReset")}
          </Button>
        </form>
      )}
    </AuthFrame>
  )
}

function ResetPassword() {
  const { t } = useTranslation()
  const [token] = useState(() => readAndClearFragmentToken("tl_reset_token"))
  const [completed, setCompleted] = useState(false)
  const [error, setError] = useState<string>()
  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const password = String(new FormData(event.currentTarget).get("password"))
    try {
      await resetPassword(token ?? "", password)
      sessionStorage.removeItem("tl_reset_token")
      setCompleted(true)
    } catch {
      setError(t("auth.resetInvalid"))
    }
  }
  return (
    <AuthFrame title={completed ? t("auth.passwordUpdated") : t("auth.resetTitle")}>
      {!token ? (
        <p className="error-text" role="alert">
          {t("auth.resetMissing")}
        </p>
      ) : null}
      {token && !completed ? (
        <form className="auth-form" onSubmit={submit}>
          <Field>
            <FieldLabel htmlFor="reset-password">{t("auth.newPassword")}</FieldLabel>
            <Input
              id="reset-password"
              name="password"
              type="password"
              autoComplete="new-password"
              minLength={8}
              maxLength={128}
              required
            />
          </Field>
          <Button type="submit">{t("auth.updatePassword")}</Button>
          {error ? (
            <p className="error-text" role="alert">
              {error}
            </p>
          ) : null}
        </form>
      ) : null}
      {completed ? (
        <Button asChild>
          <a href="/login">{t("auth.signIn")}</a>
        </Button>
      ) : null}
    </AuthFrame>
  )
}

export function readAndClearFragmentToken(storageKey?: string) {
  const fragmentToken = new URLSearchParams(window.location.hash.slice(1)).get("token")
  if (fragmentToken && storageKey) sessionStorage.setItem(storageKey, fragmentToken)
  if (window.location.hash)
    window.history.replaceState({}, "", window.location.pathname + window.location.search)
  return fragmentToken ?? (storageKey ? sessionStorage.getItem(storageKey) : null)
}

function loginReturnUrl() {
  const destination = sessionStorage.getItem("tl_auth_return")
  if (!destination) return "/login"
  if (destination.startsWith("/consent?flow=")) {
    return `/login?flow=${encodeURIComponent(new URLSearchParams(destination.split("?")[1]).get("flow") ?? "")}`
  }
  return `/login?returnTo=${encodeURIComponent(safeReturnTo(destination))}`
}

const onboardingSchema = z.object({
  ageBand: z.enum(["adult", "minor"]),
  accepted: z.boolean().refine(Boolean, "policy_required"),
})
const guardianEmailSchema = z.object({ guardianEmail: z.email() })

function Onboarding() {
  const { i18n, t } = useTranslation()
  const locale = i18n.language.startsWith("en") ? "en" : "id"
  const guardianSetup = new URLSearchParams(window.location.search).get("setup") === "guardian_only"
  const state = useQuery({
    queryKey: ["account", "onboarding", locale],
    queryFn: () => getOnboarding(locale),
    retry: false,
  })
  const onboarding = useForm<z.infer<typeof onboardingSchema>>({
    resolver: zodResolver(onboardingSchema),
    defaultValues: guardianSetup ? { ageBand: "adult", accepted: false } : { accepted: false },
  })
  const guardian = useForm<z.infer<typeof guardianEmailSchema>>({
    resolver: zodResolver(guardianEmailSchema),
    defaultValues: { guardianEmail: "" },
  })

  useEffect(() => {
    if (state.error instanceof ApiError && state.error.status === 401) {
      window.location.assign("/login?returnTo=/onboarding")
    }
  }, [state.error])
  useEffect(() => {
    const owner = state.data?.owner
    if (
      owner?.ownerStatus === "active" &&
      (owner.ageBand === "adult" || owner.ageBand === "minor")
    ) {
      onboarding.setValue("ageBand", owner.ageBand)
    }
  }, [state.data, onboarding.setValue])

  if (state.isPending) return <FullPageStatus label={t("onboarding.loading")} />
  if (state.isError || !state.data) return <FullPageError retry={() => state.refetch()} />
  const { owner, terms } = state.data
  const activeReacceptance =
    owner.ownerStatus === "active" &&
    !owner.currentTermsAccepted &&
    (owner.ageBand === "adult" || owner.ageBand === "minor")

  async function complete(values: z.infer<typeof onboardingSchema>) {
    try {
      const learnerAgeBand =
        owner.ageBand === "adult" || owner.ageBand === "minor" ? owner.ageBand : values.ageBand
      await completeOnboarding(
        guardianSetup
          ? {
              setup: "guardian_only",
              locale,
              policyVersion: terms.version,
              policyHash: terms.hash,
            }
          : {
              setup: "learner",
              ageBand: learnerAgeBand,
              locale,
              policyVersion: terms.version,
              policyHash: terms.hash,
            },
      )
      await state.refetch()
      if (guardianSetup) window.location.assign("/account")
      else if (activeReacceptance || values.ageBand === "adult") window.location.assign("/")
    } catch (reason) {
      const code = reason instanceof ApiError ? reason.code : "onboarding_failed"
      onboarding.setError("root.serverError", {
        message: t(`onboarding.errors.${code}`, {
          defaultValue: t("onboarding.completeError"),
        }),
      })
      if (code === "stale_terms" || code === "policy_unavailable") await state.refetch()
    }
  }

  if (guardianSetup) {
    if (!owner.guardianOnlyEligible) {
      return (
        <AuthFrame title={t("onboarding.guardianAccountTitle")}>
          <p role="alert">{t("onboarding.guardianAccountUnavailable")}</p>
          <Button asChild variant="secondary">
            <a href="/account">{t("guardian.openAccount")}</a>
          </Button>
        </AuthFrame>
      )
    }
    if (owner.currentTermsAccepted) {
      return (
        <AuthFrame title={t("onboarding.guardianAccountReadyTitle")}>
          <p>{t("onboarding.guardianAccountReadyBody")}</p>
          <Button asChild>
            <a href="/account">{t("guardian.openAccount")}</a>
          </Button>
        </AuthFrame>
      )
    }
    return (
      <AuthFrame title={t("onboarding.guardianAccountTitle")}>
        <form className="auth-form" onSubmit={onboarding.handleSubmit(complete)}>
          <p>{t("onboarding.guardianAccountBody")}</p>
          <PolicyDocument title={terms.content.title} body={terms.content.bodyMarkdown} />
          <Controller
            control={onboarding.control}
            name="accepted"
            render={({ field, fieldState }) => (
              <Field orientation="horizontal" data-invalid={fieldState.invalid}>
                <Checkbox
                  id="accept-guardian-account-policy"
                  checked={field.value}
                  onCheckedChange={(checked) => field.onChange(checked === true)}
                  aria-invalid={fieldState.invalid}
                />
                <FieldContent>
                  <FieldLabel htmlFor="accept-guardian-account-policy">
                    {t("onboarding.accept", { version: terms.version })}
                  </FieldLabel>
                </FieldContent>
              </Field>
            )}
          />
          <Button disabled={onboarding.formState.isSubmitting} type="submit">
            {onboarding.formState.isSubmitting
              ? t("common.saving")
              : t("onboarding.guardianAccountContinue")}
          </Button>
          {onboarding.formState.errors.root?.serverError ? (
            <p role="alert">{onboarding.formState.errors.root.serverError.message}</p>
          ) : null}
        </form>
      </AuthFrame>
    )
  }

  async function requestGuardian(values: z.infer<typeof guardianEmailSchema>) {
    try {
      await requestGuardianChallenge(values.guardianEmail, locale)
      await state.refetch()
    } catch {
      guardian.setError("root.serverError", { message: t("onboarding.guardianError") })
    }
  }

  if (owner.nextStep === "complete" && !activeReacceptance) {
    return (
      <AuthFrame title={t("onboarding.completeTitle")}>
        <p>{t("onboarding.completeBody")}</p>
        <Button asChild>
          <a href="/">{t("onboarding.openHome")}</a>
        </Button>
      </AuthFrame>
    )
  }

  if (
    owner.nextStep === "minor_guardian_email" ||
    owner.nextStep === "guardian_invitation_sent" ||
    owner.nextStep === "guardian_consent_pending"
  ) {
    const reconsent = owner.nextStep === "guardian_consent_pending"
    const replacement = owner.nextStep === "guardian_invitation_sent"
    return (
      <AuthFrame
        title={t(
          reconsent
            ? "onboarding.guardianReviewTitle"
            : replacement
              ? "onboarding.guardianPendingTitle"
              : "onboarding.guardianTitle",
        )}
      >
        <form className="auth-form" onSubmit={guardian.handleSubmit(requestGuardian)}>
          <p>
            {t(
              reconsent
                ? "onboarding.guardianReviewBody"
                : replacement
                  ? "onboarding.guardianReplacementBody"
                  : "onboarding.guardianBody",
            )}
          </p>
          <Field data-invalid={Boolean(guardian.formState.errors.guardianEmail)}>
            <FieldLabel htmlFor="guardian-email">{t("onboarding.guardianEmail")}</FieldLabel>
            <Input
              id="guardian-email"
              type="email"
              autoComplete="email"
              aria-invalid={Boolean(guardian.formState.errors.guardianEmail)}
              {...guardian.register("guardianEmail")}
            />
          </Field>
          <Button disabled={guardian.formState.isSubmitting} type="submit">
            {guardian.formState.isSubmitting
              ? t("onboarding.sending")
              : t(replacement ? "onboarding.resend" : "onboarding.send")}
          </Button>
          {guardian.formState.isSubmitSuccessful ? (
            <p role="status">{t("onboarding.guardianSent")}</p>
          ) : null}
          {guardian.formState.errors.root?.serverError ? (
            <p role="alert">{guardian.formState.errors.root.serverError.message}</p>
          ) : null}
        </form>
      </AuthFrame>
    )
  }

  return (
    <AuthFrame title={t("onboarding.title")}>
      <form className="auth-form" onSubmit={onboarding.handleSubmit(complete)}>
        {!activeReacceptance ? (
          <Controller
            control={onboarding.control}
            name="ageBand"
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor="onboarding-age-band">{t("onboarding.ageBand")}</FieldLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger id="onboarding-age-band" aria-invalid={fieldState.invalid}>
                    <SelectValue placeholder={t("onboarding.ageBandPlaceholder")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="adult">{t("onboarding.adult")}</SelectItem>
                      <SelectItem value="minor">{t("onboarding.minor")}</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <FieldDescription>{t("onboarding.ageBandDescription")}</FieldDescription>
              </Field>
            )}
          />
        ) : null}
        <PolicyDocument title={terms.content.title} body={terms.content.bodyMarkdown} />
        <Controller
          control={onboarding.control}
          name="accepted"
          render={({ field, fieldState }) => (
            <Field orientation="horizontal" data-invalid={fieldState.invalid}>
              <Checkbox
                id="accept-policy"
                checked={field.value}
                onCheckedChange={(checked) => field.onChange(checked === true)}
                aria-invalid={fieldState.invalid}
              />
              <FieldContent>
                <FieldLabel htmlFor="accept-policy">
                  {t("onboarding.accept", { version: terms.version })}
                </FieldLabel>
              </FieldContent>
            </Field>
          )}
        />
        <Button disabled={onboarding.formState.isSubmitting} type="submit">
          {onboarding.formState.isSubmitting ? t("common.saving") : t("onboarding.continue")}
        </Button>
        {onboarding.formState.errors.root?.serverError ? (
          <p role="alert">{onboarding.formState.errors.root.serverError.message}</p>
        ) : null}
      </form>
    </AuthFrame>
  )
}

function GuardianVerify() {
  const { i18n, t } = useTranslation()
  const [token] = useState(readGuardianToken)
  const locale = i18n.language.startsWith("en") ? "en" : "id"
  const review = useQuery({
    queryKey: ["guardian", "review", token, locale],
    queryFn: () => reviewGuardianChallenge(token ?? "", locale),
    enabled: Boolean(token),
    retry: false,
  })
  const form = useForm<{ purposes: string[] }>({ defaultValues: { purposes: [] } })
  const [result, setResult] = useState<"active" | "limited">()

  useEffect(() => {
    if (review.error instanceof ApiError && review.error.status === 401) {
      window.location.assign("/login?returnTo=/guardian/verify")
    }
  }, [review.error])

  async function submit({ purposes }: { purposes: string[] }) {
    if (!token || !review.data || purposes.length === 0) return
    try {
      await verifyGuardianChallenge({
        token,
        locale,
        purposes,
        reviewedPolicyVersion: review.data.version,
        reviewedPolicyHash: review.data.hash,
      })
      const active = review.data.purposes.requiredForLearning.every((purpose) =>
        purposes.includes(purpose),
      )
      sessionStorage.removeItem("tl_guardian_token")
      setResult(active ? "active" : "limited")
    } catch (reason) {
      const code = reason instanceof ApiError ? reason.code : "guardian_verify_failed"
      form.setError("root.serverError", {
        message: guardianErrorMessage(t, code),
      })
    }
  }

  if (!token) {
    return (
      <AuthFrame title={t("guardian.title")}>
        <p role="alert">{t("guardian.missingToken")}</p>
      </AuthFrame>
    )
  }
  if (review.isPending) return <FullPageStatus label={t("guardian.loading")} />
  if (review.isError || !review.data) {
    const code = review.error instanceof ApiError ? review.error.code : "guardian_review_failed"
    if (
      code === "guardian_identity_mismatch" ||
      code === "fresh_session_required" ||
      code === "guardian_email_verification_required"
    ) {
      return (
        <AuthFrame title={t("guardian.separateAccountTitle")}>
          <p>{t(`guardian.errors.${code}`)}</p>
          <Button type="button" onClick={() => void switchGuardianAccount()}>
            {t("guardian.useAnotherAccount")}
          </Button>
        </AuthFrame>
      )
    }
    if (
      code === "guardian_challenge_consumed" ||
      code === "guardian_challenge_expired" ||
      code === "guardian_challenge_locked" ||
      code === "guardian_relationship_revoked" ||
      code === "invalid_guardian_challenge"
    ) {
      return (
        <AuthFrame title={t("guardian.linkProblemTitle")}>
          <p role="alert">{t(`guardian.errors.${code}`)}</p>
          <Button asChild variant="secondary">
            <a href="/login">{t("guardian.backToSignIn")}</a>
          </Button>
        </AuthFrame>
      )
    }
    return <FullPageError retry={() => review.refetch()} />
  }
  if (result) {
    return (
      <AuthFrame title={t(result === "active" ? "guardian.activeTitle" : "guardian.limitedTitle")}>
        <p>{t(result === "active" ? "guardian.activeBody" : "guardian.limitedBody")}</p>
        <Button asChild>
          <a href="/onboarding?setup=guardian_only">{t("guardian.continueAccountSetup")}</a>
        </Button>
      </AuthFrame>
    )
  }

  return (
    <AuthFrame title={t("guardian.title")}>
      <form className="auth-form" onSubmit={form.handleSubmit(submit)}>
        <p>{t("guardian.description")}</p>
        <PolicyDocument title={review.data.policy.title} body={review.data.policy.bodyMarkdown} />
        <PurposeFields
          title={t("guardian.requiredTitle")}
          description={t("guardian.requiredBody")}
          purposes={review.data.purposes.requiredForLearning}
          form={form}
        />
        <PurposeFields
          title={t("guardian.optionalTitle")}
          description={t("guardian.optionalBody")}
          purposes={review.data.purposes.optional}
          form={form}
        />
        <Button
          disabled={form.formState.isSubmitting || form.watch("purposes").length === 0}
          type="submit"
        >
          {form.formState.isSubmitting ? t("common.saving") : t("guardian.record")}
        </Button>
        {form.formState.errors.root?.serverError ? (
          <p role="alert">{form.formState.errors.root.serverError.message}</p>
        ) : null}
      </form>
    </AuthFrame>
  )
}

async function switchGuardianAccount() {
  await signOut().catch(() => undefined)
  window.location.assign("/login?returnTo=/guardian/verify")
}

function guardianErrorMessage(t: ReturnType<typeof useTranslation>["t"], code: string) {
  if (code === "stale_guardian_policy") return t("guardian.policyChanged")
  return t(`guardian.errors.${code}`, { defaultValue: t("guardian.verifyError") })
}

function PurposeFields({
  description,
  form,
  purposes,
  title,
}: {
  description: string
  form: ReturnType<typeof useForm<{ purposes: string[] }>>
  purposes: string[]
  title: string
}) {
  const { t } = useTranslation()
  return (
    <FieldSet>
      <FieldLegend>{title}</FieldLegend>
      <FieldDescription>{description}</FieldDescription>
      {purposes.map((purpose) => (
        <Controller
          control={form.control}
          name="purposes"
          key={purpose}
          render={({ field }) => (
            <Field orientation="horizontal">
              <Checkbox
                id={`purpose-${purpose}`}
                checked={field.value.includes(purpose)}
                onCheckedChange={(checked) =>
                  field.onChange(
                    checked === true
                      ? [...field.value, purpose]
                      : field.value.filter((item) => item !== purpose),
                  )
                }
              />
              <FieldContent>
                <FieldLabel htmlFor={`purpose-${purpose}`}>
                  {t(`guardianPurposes.${purpose}`)}
                </FieldLabel>
              </FieldContent>
            </Field>
          )}
        />
      ))}
    </FieldSet>
  )
}

export function readGuardianToken() {
  return readAndClearFragmentToken("tl_guardian_token")
}

type ConsentState = {
  clientName: string
  redirectHost: string
  requestedScopes: string[]
  grantableScopes: string[]
}
function Consent() {
  const { t } = useTranslation()
  const flow = new URLSearchParams(window.location.search).get("flow") ?? ""
  const [state, setState] = useState<ConsentState>()
  const [error, setError] = useState<string>()
  const [pending, setPending] = useState<"allow" | "deny">()
  useEffect(() => {
    fetch(`${apiOrigin}/oauth/authorize/resume`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ flow }),
    })
      .then(async (response) => {
        if (!response.ok) throw new Error()
        setState(await response.json())
      })
      .catch(() => setError(t("consent.invalid")))
  }, [flow, t])
  async function decide(action: "allow" | "deny") {
    setPending(action)
    setError(undefined)
    try {
      const response = await fetch(`${apiOrigin}/oauth/authorize/decision`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ flow, action }),
      })
      const result = (await response.json()) as { redirectTo?: string }
      if (!response.ok || !result.redirectTo) return setError(t("consent.decisionError"))
      window.location.assign(result.redirectTo)
    } catch {
      setError(t("consent.decisionError"))
    } finally {
      setPending(undefined)
    }
  }
  return (
    <AuthFrame title={t("consent.title")}>
      {state ? (
        <>
          <p>{t("consent.return", { client: state.clientName, host: state.redirectHost })}</p>
          <ul className="scope-list">
            {state.grantableScopes.map((scope) => (
              <li key={scope}>{scope.replace("companion.", "")}</li>
            ))}
          </ul>
          <div className="button-row">
            <Button disabled={Boolean(pending)} type="button" onClick={() => void decide("allow")}>
              {pending === "allow" ? t("consent.allowing") : t("consent.allow")}
            </Button>
            <Button
              variant="secondary"
              disabled={Boolean(pending)}
              type="button"
              onClick={() => void decide("deny")}
            >
              {pending === "deny" ? t("consent.denying") : t("consent.deny")}
            </Button>
          </div>
        </>
      ) : (
        <p>{t("consent.loading")}</p>
      )}
      {error ? (
        <p className="error-text" role="alert">
          {error}
        </p>
      ) : null}
    </AuthFrame>
  )
}

function FullPageStatus({ label }: { label: string }) {
  return (
    <main className="center-state" aria-live="polite">
      <span className="orbit-dot" /> <p>{label}</p>
    </main>
  )
}
function FullPageError({ retry }: { retry: () => void }) {
  const { t } = useTranslation()
  return (
    <main className="center-state">
      <p>{t("states.errorBody")}</p>
      <Button type="button" onClick={retry}>
        {t("retry")}
      </Button>
    </main>
  )
}
function InlineError({ retry }: { retry: () => void }) {
  const { t } = useTranslation()
  return (
    <section className="paper-card empty-card" role="alert">
      <h2>{t("states.errorTitle")}</h2>
      <Button variant="secondary" type="button" onClick={retry}>
        {t("retry")}
      </Button>
    </section>
  )
}
function EmptyState() {
  const { t } = useTranslation()
  return <PageEmpty title={t("noTopics")} description={t("home.noTopicsBody")} />
}
function DashboardSkeleton() {
  const { t } = useTranslation()
  return (
    <div className="skeleton-stack" role="status" aria-label={t("states.loading")}>
      <span />
      <span />
      <span />
    </div>
  )
}

function NotFound() {
  const { t } = useTranslation()
  return (
    <AuthFrame title={t("notFound.title")}>
      <p>{t("notFound.body")}</p>
      <Button asChild>
        <a href="/">{t("notFound.action")}</a>
      </Button>
    </AuthFrame>
  )
}
